import React from 'react';
import HypertextLink from './HypertextLink';
import { generateComponentDocs, generateStoryDocs } from '../../utils/documentationUtils';

export default {
  title: 'Components/HypertextLink',
  component: HypertextLink,
  parameters: {
    layout: 'centered',
    ...generateComponentDocs(
      // What problem does it solve?
      '\nThe `HypertextLink` component provides users with a navigational link that can be styled and customized. It supports various use cases such as navigation, redirection, and linking to external resources.\n\n',
      // When & how to use it?
      `\nUse the \`HypertextLink\` component whenever you need to provide a clickable link for navigation or redirection. This includes:
      \n- Linking to other pages within the app
      \n- Redirecting to external resources
      \n- Providing contextual links within content
      \n\nIt is flexible and customizable to match different UI and UX requirements.\n\n`,
      // Current Usage
      `\nThe \`HypertextLink\` component is used across the application, including:
      \n- Navigation links in headers and footers
      \n- Contextual links within content
      \n- Links to external resources
      \n\nIt ensures consistency and accessibility in navigation across the app.\n\n`,
      // Related Components
      `\n- **[Button](http://localhost:6006/?path=/docs/components-button-button--docs)**: Used for actions that require a button instead of a link.
      \n- **[TextInput](http://localhost:6006/?path=/docs/components-forminputs-textinput--docs)**: Often used alongside links in forms and content.
      \n\n`,
    ),
  },
  tags: ['autodocs'],
};

const Template = (args) => <HypertextLink {...args} />;

export const Default = Template.bind({});
Default.args = {
  href: '/users/password/new',
  children: 'Forgot password?',
};
Default.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThe `Default` variant is used as the base link style for general navigation and redirection.\n\n',
  // Where is it currently used on the product?
  '\nThis variant is commonly used for navigation links in headers, footers, and content across the app.\n\n',
);

export const CustomText = Template.bind({});
CustomText.args = {
  href: '/custom-link',
  children: 'Custom Link Text',
};
CustomText.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThe `CustomText` variant is used when a link requires custom text to provide context or specific information.\n\n',
  // Where is it currently used on the product?
  '\nThis variant is used for contextual links within content and navigation.\n\n',
);

export const WithClassName = Template.bind({});
WithClassName.args = {
  href: '/users/password/new',
  children: 'Forgot password?',
  className: 'custom-class',
};
WithClassName.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThe `WithClassName` variant is used when a link requires custom styling through a class name.\n\n',
  // Where is it currently used on the product?
  '\nThis variant is used for links that need specific styling to match the design requirements.\n\n',
);
